import { useState, useEffect, useRef, useCallback } from "react";
import { X } from "lucide-react";

export function BarcodeScanner({ onScan, onClose }) {
  const scannerRef = useRef(null);
  const onScanRef = useRef(onScan);
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraCandidatesRef = useRef([]);
  const retryStepRef = useRef(0);
  const previewStartedRef = useRef(false);
  const previewTimeoutRef = useRef(null);
  const mountedRef = useRef(false);
  const closingRef = useRef(false);
  const startingRef = useRef(false);
  const [cameraState, setCameraState] = useState("starting");
  const [cameraMessage, setCameraMessage] = useState("Sto aprendo la fotocamera posteriore...");

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  const clearPreviewTimeout = useCallback(() => {
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = null;
    }
  }, []);

  const destroyScanner = useCallback(async () => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    startingRef.current = false;
    previewStartedRef.current = false;
    clearPreviewTimeout();

    if (!scanner) return;

    try {
      await scanner.stop();
    } catch { /* scanner may already be stopped */ }

    try {
      scanner.destroy();
    } catch { /* scanner may already be destroyed */ }
  }, [clearPreviewTimeout]);

  const startScanner = useCallback(async () => {
    if (startingRef.current || !videoRef.current) return;
    startingRef.current = true;
    closingRef.current = false;
    previewStartedRef.current = false;
    clearPreviewTimeout();
    setCameraState("starting");
    setCameraMessage(retryStepRef.current > 0 ? "Sto provando una fotocamera alternativa..." : "Sto aprendo la fotocamera posteriore...");

    try {
      const { default: QrScanner } = await import("qr-scanner");
      if (!mountedRef.current || closingRef.current || !videoRef.current) return;

      const hasCamera = await QrScanner.hasCamera().catch(() => true);
      if (!hasCamera) throw new Error("no-camera");

      const onSuccess = async (text) => {
        if (closingRef.current) return;
        closingRef.current = true;
        await destroyScanner();
        if (mountedRef.current) onScanRef.current?.(text);
      };

      const cameras = await QrScanner.listCameras(true).catch(() => QrScanner.listCameras().catch(() => []));
      const backCamera = cameras.find((camera) => /back|rear|environment|posteriore|trasera/i.test(camera.label || ""));
      const alternativeCamera = cameras.find((camera) => camera.id !== backCamera?.id);
      const cameraCandidates = [backCamera?.id || "environment", alternativeCamera?.id, "user"].filter(Boolean);
      cameraCandidatesRef.current = cameraCandidates.filter((candidate, index, arr) => arr.indexOf(candidate) === index);

      const preferredCamera = cameraCandidatesRef.current[Math.min(retryStepRef.current, cameraCandidatesRef.current.length - 1)] || "environment";

      await destroyScanner();
      const scanner = new QrScanner(videoRef.current, (result) => onSuccess(typeof result === "string" ? result : result?.data), {
        onDecodeError: () => {},
        preferredCamera,
        maxScansPerSecond: 12,
        returnDetailedScanResult: true,
        highlightScanRegion: false,
        highlightCodeOutline: false,
      });

      scannerRef.current = scanner;
      scanner.setInversionMode("both");
      videoRef.current.setAttribute("playsinline", "true");
      videoRef.current.muted = true;

      await scanner.start();
      setCameraMessage("Attendo l’anteprima della fotocamera...");
      previewTimeoutRef.current = setTimeout(async () => {
        if (previewStartedRef.current || closingRef.current || !mountedRef.current) return;
        setCameraState("error");
        setCameraMessage("La fotocamera non ha mostrato l’anteprima. Premi Riprova oppure cambia metodo.");
        await destroyScanner();
      }, 3000);
    } catch {
      await destroyScanner();
      if (!mountedRef.current || closingRef.current) return;
      setCameraState("error");
      setCameraMessage("Fotocamera non pronta. Premi Riprova oppure usa Scatta foto del QR.");
    } finally {
      startingRef.current = false;
    }
  }, [clearPreviewTimeout, destroyScanner]);

  useEffect(() => {
    mountedRef.current = true;
    retryStepRef.current = 0;
    void startScanner();

    return () => {
      mountedRef.current = false;
      closingRef.current = true;
      void destroyScanner();
    };
  }, [destroyScanner, startScanner]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const handlePreviewStarted = () => {
      previewStartedRef.current = true;
      clearPreviewTimeout();
      if (!mountedRef.current || closingRef.current) return;
      setCameraState("ready");
      setCameraMessage("Inquadra il QR dentro il riquadro.");
    };

    video.addEventListener("loadeddata", handlePreviewStarted);
    video.addEventListener("playing", handlePreviewStarted);

    return () => {
      video.removeEventListener("loadeddata", handlePreviewStarted);
      video.removeEventListener("playing", handlePreviewStarted);
    };
  }, [clearPreviewTimeout]);

  const handleClose = async () => {
    closingRef.current = true;
    retryStepRef.current = 0;
    await destroyScanner();
    onClose();
  };

  const handleRetry = async () => {
    closingRef.current = false;
    retryStepRef.current = Math.min(retryStepRef.current + 1, Math.max(cameraCandidatesRef.current.length - 1, 0));
    await destroyScanner();
    void startScanner();
  };

  const handleFileScan = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    closingRef.current = false;
    setCameraState("starting");
    setCameraMessage("Sto leggendo il QR dalla foto...");

    try {
      await destroyScanner();
      const { default: QrScanner } = await import("qr-scanner");
      const result = await QrScanner.scanImage(file, { returnDetailedScanResult: true });
      onScan(typeof result === "string" ? result : result?.data);
    } catch {
      await destroyScanner();
      if (!mountedRef.current) return;
      setCameraState("error");
      setCameraMessage("QR non letto. Prova con più luce oppure avvicina meglio il codice.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black z-[100] flex flex-col" data-testid="barcode-scanner">
      <div className="flex items-center justify-between p-4 bg-black/80">
        <p className="text-white font-bold text-sm uppercase tracking-wider">Scansiona Barcode</p>
        <button onClick={handleClose} className="p-2 text-white" data-testid="close-scanner"><X size={24} /></button>
      </div>
      <div className="flex-1 flex flex-col items-center justify-center px-4 gap-4">
        <div className="w-full max-w-sm aspect-square bg-black rounded-lg border border-zinc-700 overflow-hidden" data-testid="qr-reader-frame">
          <video ref={videoRef} className="w-full h-full object-cover" muted playsInline data-testid="qr-reader-container" />
        </div>
        <div className="text-center max-w-sm">
          <p className={`text-sm font-bold ${cameraState === "error" ? "text-red-300" : "text-white"}`} data-testid="scanner-status-message">{cameraMessage}</p>
          <p className="text-xs text-zinc-400 mt-2">Usa luce buona e tieni il QR fermo a circa 15-20 cm.</p>
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <button onClick={handleRetry} className="h-11 px-5 bg-[#FF5200] text-white text-xs font-bold uppercase rounded-sm" data-testid="retry-scanner-btn">
            {cameraState === "ready" ? "Cambia fotocamera" : "Riprova fotocamera"}
          </button>
          <button onClick={() => fileInputRef.current?.click()} className="h-11 px-5 border border-zinc-600 text-white text-xs font-bold uppercase rounded-sm" data-testid="manual-qr-capture-btn">
            Scatta foto del QR
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileScan} data-testid="manual-qr-file-input" />
        </div>
      </div>
    </div>
  );
}
